import { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { DataKeys } from "../../types";
import educationHeroInstructor from "../../image/education/education-hero-instructor.png";
import { getLocalized, HealthLang, LocalizedString } from "../health/healthTypes";
import { healthHeroLoopWrap } from "../health/healthLayout";
import "./educationHeroWaveform.css";
import "./educationHeroOrbit.css";
import "./educationHeroCallouts.css";

interface EducationPracticeHeroPanelProps {
    visual: DataKeys;
    lang: HealthLang;
}

const orbitDots = [
    { duration: 65, delay: -6, size: 5, soft: false, reverse: false },
    { duration: 88, delay: -24, size: 9, soft: true, reverse: true },
    { duration: 55, delay: -36, size: 4, soft: false, reverse: false },
    { duration: 78, delay: -15, size: 7, soft: true, reverse: false },
    { duration: 98, delay: -48, size: 10, soft: false, reverse: true },
    { duration: 72, delay: -30, size: 6, soft: true, reverse: false },
] as const;

/** Match Health hero orbit card entrance (HealthLoopDiagram). */
const HERO_ENTRANCE_EASE = [0.22, 1, 0.36, 1] as const;
const HERO_ENTRANCE_DURATION = 0.8;
const HERO_ENTRANCE_STAGGER = 0.1;
const HERO_ENTRANCE_BASE_DELAY = 0.12;

function EducationHeroEntrance({
    stageIndex,
    className = "",
    children,
    as = "div",
}: {
    stageIndex: number;
    className?: string;
    children: ReactNode;
    as?: "div" | "li";
}) {
    const reduceMotion = useReducedMotion();
    const delay = HERO_ENTRANCE_BASE_DELAY + stageIndex * HERO_ENTRANCE_STAGGER;
    const MotionTag = as === "li" ? motion.li : motion.div;

    if (reduceMotion) {
        const Tag = as === "li" ? "li" : "div";
        return <Tag className={className}>{children}</Tag>;
    }

    return (
        <MotionTag
            className={className}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: HERO_ENTRANCE_DURATION,
                delay,
                ease: HERO_ENTRANCE_EASE,
            }}
        >
            {children}
        </MotionTag>
    );
}

function EducationHeroOrbit() {
    return (
        <div
            className="education-hero-orbit absolute left-1/2 top-[42%] hidden h-[30rem] w-[30rem] -translate-x-1/2 -translate-y-1/2 lg:block xl:h-[32rem] xl:w-[32rem]"
            aria-hidden="true"
        >
            <div className="education-hero-orbit-ring" />
            {orbitDots.map((dot, index) => (
                <div
                    key={index}
                    className="education-hero-orbit-track"
                    style={{
                        animationDuration: `${dot.duration}s`,
                        animationDelay: `${dot.delay}s`,
                        animationDirection: dot.reverse ? "reverse" : "normal",
                    }}
                >
                    <span
                        className={`education-hero-orbit-dot ${dot.soft ? "education-hero-orbit-dot--soft" : ""}`}
                        style={{ width: `${dot.size}px`, height: `${dot.size}px` }}
                    />
                </div>
            ))}
        </div>
    );
}

const calloutIcons = {
    conversations: (
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
        </svg>
    ),
    coaching: (
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="8" r="3.2" />
            <path d="M6 19c1.8-2.5 3.6-3.5 6-3.5s4.2 1 6 3.5" />
            <path d="M16 3.5 18 2v4" />
            <path d="M20 4.5l-1.5 1.5" />
        </svg>
    ),
    progress: (
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 19V5" />
            <path d="M4 19h16" />
            <path d="M8 16V10" />
            <path d="M12 16V7" />
            <path d="M16 16v-5" />
        </svg>
    ),
} as const;

function ChatAvatar({ tone }: { tone: "patient" | "learner" | "instructor" }) {
    const toneClass =
        tone === "learner"
            ? "bg-teal-100 text-teal-700"
            : tone === "instructor"
              ? "bg-grey-100 text-grey-600"
              : "bg-rose-50 text-rose-400";

    return (
        <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${toneClass}`}
            aria-hidden="true"
        >
            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c2.2-3.5 4.8-5 8-5s5.8 1.5 8 5" />
            </svg>
        </span>
    );
}

function HeroCallout({
    icon,
    title,
    text,
}: {
    icon: ReactNode;
    title: string;
    text: string;
}) {
    return (
        <>
            <span className="education-hero-callout__icon flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-500 text-white shadow-[0_4px_12px_rgba(3,124,132,0.25)]">
                {icon}
            </span>
            <div className="education-hero-callout__body w-full min-w-0">
                <p className="education-hero-callout__title font-semibold leading-snug text-grey-900">
                    {title}
                </p>
                <p className="education-hero-callout__text mt-0.5 leading-relaxed text-grey-500">
                    {text}
                </p>
            </div>
        </>
    );
}

function AudioWaveform() {
    const bars = [3, 5, 8, 6, 9, 5, 7, 4, 6, 8, 5, 3];

    return (
        <div className="flex h-4 max-h-4 items-end gap-[3px] overflow-hidden" aria-hidden="true">
            {bars.map((height, index) => (
                <span
                    key={index}
                    className="education-hero-wave-bar w-[3px] shrink-0 rounded-full bg-teal-400/80"
                    style={{
                        height: `${Math.min(height + 4, 13)}px`,
                        animationDelay: `${index * 0.09}s`,
                    }}
                />
            ))}
        </div>
    );
}

export function EducationPracticeHeroPanel({ visual, lang }: EducationPracticeHeroPanelProps) {
    const callouts: DataKeys[] = (visual?.callouts as DataKeys[]) ?? [];
    const cardTitle = getLocalized(visual?.cardTitle, lang) || "Guided Practice";
    const patientRole = getLocalized(visual?.patientRole, lang) || "AI Patient";
    const learnerRole = getLocalized(visual?.learnerRole, lang) || "Learner";
    const instructorRole = getLocalized(visual?.instructorRole, lang) || "Virtual Instructor";

    const chatLines = [
        {
            role: patientRole,
            message: getLocalized(visual?.patientLine, lang),
            tone: "patient" as const,
            bubbleClass: "bg-white text-grey-700 shadow-sm",
        },
        {
            role: learnerRole,
            message: getLocalized(visual?.learnerLine, lang),
            tone: "learner" as const,
            bubbleClass: "bg-teal-50/90 text-teal-900",
        },
        {
            role: instructorRole,
            message: getLocalized(visual?.instructorHint, lang),
            tone: "instructor" as const,
            bubbleClass: "bg-white text-grey-600 shadow-sm",
        },
    ].filter((line) => line.message);

    return (
        <div className={`${healthHeroLoopWrap} relative`}>
            <div
                className="relative mx-auto w-full min-w-0 max-w-[28rem] overflow-visible px-1 py-4 sm:max-w-[30rem] sm:px-0 lg:max-w-[34rem] lg:py-0 xl:max-w-[38rem]"
                aria-hidden="true"
            >
                <div
                    className="pointer-events-none absolute left-1/2 top-[42%] h-[19rem] w-[19rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(87,196,203,0.22)_0%,rgba(87,196,203,0.07)_42%,transparent_72%)] sm:h-[24rem] sm:w-[24rem] lg:h-[30rem] lg:w-[30rem]"
                    aria-hidden="true"
                />

                <EducationHeroOrbit />

                <div className="relative min-w-0 max-w-full pt-2 lg:pr-[9.75rem] xl:pr-[10.25rem]">
                    <div className="relative flex flex-col items-center gap-3 sm:flex-row sm:items-end sm:justify-center sm:gap-0 lg:justify-start">
                        <EducationHeroEntrance
                            stageIndex={0}
                            className="relative z-10 w-full max-w-[12.75rem] shrink-0 sm:max-w-[13rem] md:max-w-[14rem] lg:translate-y-2"
                        >
                            <article className="rounded-2xl border border-grey-100/80 bg-white/95 p-3.5 shadow-[0_12px_36px_rgba(16,24,40,0.08)] backdrop-blur-[1px]">
                                <p className="text-xs font-semibold text-teal-600">{cardTitle}</p>
                                <div className="mt-3 space-y-2.5 text-left [text-align:left]">
                                    {chatLines.map((line) => (
                                        <div key={line.role} className="flex items-start gap-2">
                                            <ChatAvatar tone={line.tone} />
                                            <div className="min-w-0 flex-1 text-left [text-align:left]">
                                                <p className="text-left text-[9px] font-semibold uppercase tracking-wide text-grey-400 [text-align:left]">
                                                    {line.role}
                                                </p>
                                                <p
                                                    className={`mt-0.5 rounded-lg px-2 py-1.5 text-left text-[11px] leading-snug [text-align:left] ${line.bubbleClass}`}
                                                >
                                                    {line.message}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3 flex justify-center gap-1" aria-hidden="true">
                                    <span className="h-1 w-1 rounded-full bg-teal-400" />
                                    <span className="h-1 w-1 rounded-full bg-grey-200" />
                                    <span className="h-1 w-1 rounded-full bg-grey-200" />
                                </div>
                            </article>
                        </EducationHeroEntrance>

                        <EducationHeroEntrance
                            stageIndex={1}
                            className="relative z-20 flex flex-col items-center sm:-ml-11 sm:-translate-y-2 md:-ml-9 lg:-ml-10 lg:-translate-x-2 lg:-translate-y-5"
                        >
                            <div className="h-[16.5rem] w-[9.5rem] overflow-hidden rounded-2xl border border-white/90 bg-[#f4f6f8] shadow-[0_20px_48px_rgba(16,24,40,0.16)] sm:h-[17rem] sm:w-[10rem] md:h-[19rem] md:w-[11rem] lg:h-[21rem] lg:w-[12rem]">
                                <img
                                    src={educationHeroInstructor}
                                    alt=""
                                    className="h-full w-full object-cover object-[50%_10%]"
                                    loading="eager"
                                    decoding="async"
                                />
                            </div>
                            <div className="mt-2 flex items-center gap-2 rounded-full border border-grey-100/90 bg-white/95 px-3 py-1.5 shadow-[0_6px_16px_rgba(16,24,40,0.06)]">
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M12 1a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                        <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
                                        <path d="M12 19v4" />
                                    </svg>
                                </span>
                                <AudioWaveform />
                            </div>
                        </EducationHeroEntrance>
                    </div>

                    {callouts.length > 0 && (
                        <ul className="education-hero-callout-list relative z-40 mt-6 w-full min-w-0 list-none pl-0 sm:mt-8 lg:absolute lg:right-0 lg:top-4 lg:mt-0 lg:w-[9rem] lg:max-w-[9rem] xl:top-6 xl:w-[9.5rem] xl:max-w-[9.5rem]">
                            {callouts.map((callout, index) => {
                                const iconKey = (callout.id as keyof typeof calloutIcons) ?? "conversations";
                                return (
                                    <EducationHeroEntrance
                                        key={(callout.id as string) ?? index}
                                        stageIndex={2 + index}
                                        as="li"
                                        className="education-hero-callout flex w-full min-w-0 flex-col gap-2 max-lg:gap-1.5"
                                    >
                                        <HeroCallout
                                            icon={calloutIcons[iconKey] ?? calloutIcons.conversations}
                                            title={getLocalized(callout.title as LocalizedString, lang)}
                                            text={getLocalized(callout.text as LocalizedString, lang)}
                                        />
                                    </EducationHeroEntrance>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
